import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Sparkles, Building2, ArrowRight, User, Users, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { navigateToCheckout } from "@/lib/externalBrowser";
import { SUBSCRIPTION_TIERS, MUSIK_KITA_PAKET_PRICE_EUR } from "@/config/subscriptionTiers";

interface PricingThreeTiersProps {
  isAlreadyMember: boolean;
  isLoggedIn: boolean | null;
}

const starterFeatures = [
  "Starter-Bundle (1 Kurs + 1 Kreativpaket)",
  "1 Journalartikel",
  "5 Miko KI-Anfragen pro Monat",
  "Newsletter mit Tipps & Inspiration",
];

const membershipFeatures = [
  "Alle Online-Kurse komplett",
  "Alle Kreativpakete",
  "Alle Journalartikel",
  "Liederwerkstatt Live + Aufzeichnungen",
  "Live-Webinare + Aufzeichnungen",
  "Community-Zugang",
  "Unbegrenzte Miko KI-Anfragen",
  "Zertifikate für alle Kurse",
  "Priority Support",
];

const kitaExtraFeatures = [
  "Team-Zugang für bis zu 15 Kolleg:innen",
  "Kostenloser Eltern-Zugang",
  "MusiKonzept-Gütesiegel + Verzeichniseintrag",
  "Persönliche Betreuung (fester Ansprechpartner)",
];

const PricingThreeTiers = ({ isAlreadyMember, isLoggedIn }: PricingThreeTiersProps) => {
  const navigate = useNavigate();
  const monthlyPrice = SUBSCRIPTION_TIERS.pro.pricing.monthly.price;
  const yearlyPrice = SUBSCRIPTION_TIERS.pro.pricing.yearly.price;

  const handleFreeStart = () => {
    if (isLoggedIn) {
      navigate("/mein-bereich");
    } else {
      navigate("/registrieren");
    }
  };

  const handleMembership = () => {
    if (isAlreadyMember) return;
    navigateToCheckout(`/abo-checkout/pro?interval=monthly`, navigate);
  };

  const handleDirectPurchase = () => {
    if (isAlreadyMember) return;
    navigateToCheckout(`/abo-checkout/pro?interval=monthly&mode=direct`, navigate);
  };


  return (
    <>
      <section className="py-24 bg-gradient-to-b from-background via-muted/10 to-background">
        <div className="container mx-auto px-6 sm:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto space-y-4 animate-fade-in">
            <Badge variant="secondary" className="rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
              Drei Wege zu MusiKonzept
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold">
              Wähle, was zu dir passt
            </h2>
            <p className="text-muted-foreground text-lg">
              Vom kostenlosen Reinschnuppern bis zum kompletten Paket für die ganze Kita.
            </p>
          </div>

          <div className="grid gap-6 md:gap-8 md:grid-cols-3 max-w-6xl mx-auto items-stretch pt-8 md:pt-16">
            {/* Säule 1: Kostenlos */}
            <Card className="group relative flex flex-col border-border/60 hover:border-primary/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in">
              <CardContent className="p-8 lg:p-10 flex flex-col flex-1 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                      <User className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Für Neugierige
                    </p>
                  </div>
                  <h3 className="text-2xl font-bold">Kostenlos</h3>
                  <div className="flex items-baseline gap-1 pt-1">
                    <span className="text-5xl font-bold tracking-tight">0 €</span>
                    <span className="text-sm text-muted-foreground ml-1">für immer</span>
                  </div>
                </div>

                <div className="h-px bg-border/60" />

                <ul className="space-y-3 text-sm flex-1">
                  {starterFeatures.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={handleFreeStart}
                  variant="outline"
                  size="lg"
                  className="w-full rounded-full font-semibold group-hover:border-primary group-hover:text-primary transition-colors"
                >
                  Kostenlos starten
                </Button>
              </CardContent>
            </Card>

            {/* Säule 2: Mitgliedschaft (hervorgehoben) */}
            <Card className="group relative flex flex-col border-2 border-primary/70 shadow-2xl md:-translate-y-4 md:scale-[1.03] hover:shadow-[0_25px_60px_-15px_hsl(var(--primary)/0.4)] hover:-translate-y-5 transition-all duration-300 animate-fade-in">
              <div className="absolute inset-0 overflow-hidden rounded-[inherit] pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
                <div className="absolute -top-24 -right-24 w-56 h-56 bg-primary/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-24 -left-24 w-56 h-56 bg-accent/15 rounded-full blur-3xl" />
              </div>


              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg px-4 py-1.5 rounded-full border-0">
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  Beliebteste
                </Badge>
              </div>

              <CardContent className="relative p-8 lg:p-10 pt-10 flex flex-col flex-1 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                      <Users className="w-4 h-4 text-primary" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                      Für dich · bis zu 3 Personen
                    </p>
                  </div>
                  <h3 className="text-2xl font-bold">Mitgliedschaft</h3>
                  <div className="flex items-baseline gap-1 pt-1">
                    <span className="text-5xl font-bold tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      {monthlyPrice} €
                    </span>
                    <span className="text-sm text-muted-foreground ml-1">/ Monat</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    oder {yearlyPrice} €/Jahr (2 Monate sparen)
                  </p>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

                <ul className="space-y-3 text-sm flex-1">
                  {membershipFeatures.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="space-y-2">
                  <Button
                    onClick={handleMembership}
                    disabled={isAlreadyMember}
                    size="lg"
                    className="w-full rounded-full font-semibold bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    {isAlreadyMember ? "Du bist bereits Mitglied" : "2 Wochen für 1 € testen"}
                  </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Jederzeit kündbar · Danach {monthlyPrice} €/Monat
                </p>
                {!isAlreadyMember && (
                  <button
                    onClick={handleDirectPurchase}
                    className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                  >
                    Direkt buchen
                  </button>
                )}
              </div>

              </CardContent>
            </Card>

            {/* Säule 3: Musik-Kita-Paket */}
            <Card className="group relative flex flex-col border-border/60 hover:border-accent/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in">
              <CardContent className="p-8 lg:p-10 flex flex-col flex-1 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-accent/15 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-accent" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                      Für Kitas & Einrichtungen
                    </p>
                  </div>
                  <h3 className="text-2xl font-bold">Musik-Kita-Paket</h3>
                  <div className="flex items-baseline gap-1 pt-1">
                    <span className="text-5xl font-bold tracking-tight">{MUSIK_KITA_PAKET_PRICE_EUR} €</span>
                    <span className="text-sm text-muted-foreground ml-1">/ Jahr</span>
                  </div>
                </div>

                <div className="h-px bg-border/60" />

                <div className="flex-1 space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    Alles aus der Mitgliedschaft, plus:
                  </p>
                  <ul className="space-y-3 text-sm">
                    {kitaExtraFeatures.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-2">
                  <Button
                    onClick={() => navigate("/musik-kita")}
                    size="lg"
                    variant="outline"
                    className="w-full rounded-full font-semibold border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    Mehr erfahren
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <button
                    onClick={() => navigate("/musik-kita-paket")}
                    className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                  >
                    Direkt buchen
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Dunkler Marken-Gradient-Moment: Einzel oder Kita? */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-[hsl(190_60%_18%)] text-primary-foreground">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-foreground/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 sm:px-8 py-20 md:py-24 relative">
          <div className="max-w-4xl mx-auto text-center space-y-6 animate-fade-in">
            <Badge className="bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/20 border border-primary-foreground/20 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
              Kurz erklärt
            </Badge>
            <h3 className="text-3xl md:text-4xl font-bold">Einzel oder Kita?</h3>
            <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-3xl mx-auto">
              Die <strong className="text-primary-foreground">Mitgliedschaft</strong> ist für dich persönlich –
              teilbar mit bis zu 2 weiteren Personen (insgesamt 3). Das{" "}
              <strong className="text-primary-foreground">Musik-Kita-Paket</strong> ist für die ganze
              Einrichtung: komplettes Team, kostenloser Eltern-Zugang und das sichtbare
              MusiKonzept-Gütesiegel.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 pt-8 max-w-2xl mx-auto text-left">
              <div className="rounded-2xl bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/15 p-6 hover:bg-primary-foreground/15 transition-colors">
                <Users className="w-6 h-6 mb-3 text-accent" />
                <p className="font-semibold mb-1">Mitgliedschaft</p>
                <p className="text-sm text-primary-foreground/80">
                  Für dich – teilbar mit bis zu 2 weiteren Personen (insgesamt 3).
                </p>
              </div>
              <div className="rounded-2xl bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/15 p-6 hover:bg-primary-foreground/15 transition-colors">
                <Home className="w-6 h-6 mb-3 text-accent" />
                <p className="font-semibold mb-1">Musik-Kita-Paket</p>
                <p className="text-sm text-primary-foreground/80">
                  Für die ganze Einrichtung inkl. Team, Eltern & Siegel.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingThreeTiers;
